import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatDate } from '../utils/dateFormat';

const LEAVE_REQUEST_STATUS_LABELS: Record<number, string> = {
  0: 'Draft',
  1: 'Pending approval',
  2: 'Approved',
  3: 'Withdrawn',
  4: 'Pending senior approval',
  5: 'Pending HR approval',
};

export interface RecalculateWorkingDaysResult {
  updatedCount: number;
  unchangedCount: number;
  skippedCount: number;
  updated: Array<{
    leaveRequestId: string;
    requesterId: string;
    requesterName: string;
    status: number;
    fromDate: string;
    toDate: string;
    previousWorkingDays: number | null;
    newWorkingDays: number;
  }>;
  skipped: Array<{ leaveRequestId: string; error: string }>;
}

interface RecalculateWorkingDaysResultDialogProps {
  open: boolean;
  onClose: () => void;
  result: RecalculateWorkingDaysResult | null;
}

export function RecalculateWorkingDaysResultDialog({
  open,
  onClose,
  result,
}: RecalculateWorkingDaysResultDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Recalculation result</DialogTitle>
      <DialogContent>
        {result && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Updated: <strong>{result.updatedCount}</strong> · Unchanged:{' '}
              <strong>{result.unchangedCount}</strong>
              {result.skippedCount > 0 ? (
                <>
                  {' '}
                  · Skipped (errors): <strong>{result.skippedCount}</strong>
                </>
              ) : null}
            </Typography>
            {result.updated.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Rows updated
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>From</TableCell>
                        <TableCell>To</TableCell>
                        <TableCell align="right">Before</TableCell>
                        <TableCell align="right">After</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.updated.map((row) => (
                        <TableRow key={row.leaveRequestId}>
                          <TableCell>{row.requesterName}</TableCell>
                          <TableCell>
                            {LEAVE_REQUEST_STATUS_LABELS[row.status] ?? String(row.status)}
                          </TableCell>
                          <TableCell>{formatDate(row.fromDate)}</TableCell>
                          <TableCell>{formatDate(row.toDate)}</TableCell>
                          <TableCell align="right">
                            {row.previousWorkingDays != null ? row.previousWorkingDays : '—'}
                          </TableCell>
                          <TableCell align="right">{row.newWorkingDays}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            {result.skipped.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }} color="error">
                  Skipped
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Request ID</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.skipped.map((row) => (
                        <TableRow key={row.leaveRequestId}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.leaveRequestId}</TableCell>
                          <TableCell>{row.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
